<template>

	<div class="chart-container" :class="{expandible, expanded, 'full-width': fullWidth, pie: ['pie', 'donut'].includes(type) }">
		<AppCard :no-header="!title" :class="{'no-padding': !hideYAxis, 'no-data': isEmpty}">
			<template v-slot:body>
				<div v-show="(caption || value) && !isEmpty" class="data-container">
					<div v-if="caption || finalIncrement" class="row centered flex-start container-caption-head">
						<p :class="{'full-width': !increment}" class="info-caption">{{ caption }}</p>
						<p v-show="finalLastPeriodValue" :class="{negative: increment && increment < 0}" :title="parsedIncrement" class="info-increment">{{ finalIncrement }}</p>
					</div>
					<div v-if="(value !== null) || (lastPeriodValue !== null)" class="row align-end space-between container-caption">
						<p :class="{'full-width': !lastPeriodValue}" class="info-value">{{ finalValue }}</p>
						<p v-show="!!lastPeriodValue" class="info-last-period-value">{{ finalLastPeriodValue }}</p>
					</div>
				</div>

				<div v-if="badgeLive" class="badge-live-container">
					<div class="badge-live">{{ getString("live") }}</div>
				</div>

				<div class="chart-svg-container" :class="{'pt-20': showControls}">
					<ApexChart ref="chart" :height="height" :series="type==='rangeArea' ? toRaw(initialSeries) : initialSeries" :options="getOptions()"></ApexChart>
					<div
						class="empty-chart-image"
						v-html="getIcon('chartPlaceholder')+'<p>'+getString('missing_data')+'</p>'"
					>
					</div>
				</div>

			</template>
			<template v-slot:header>
				<slot name="custom-header" :expanded="expanded" :clickExpand="clickExpand" :expandible="expandible" :isEmpty="isEmpty">
					<div class="header-title-container">
						<h3>{{ title }}</h3>
						<AppInfoButton v-if="infoButton">
							{{ infoButton }}
						</AppInfoButton>
					</div>
					<div>
						<AppButtonIcon v-if="expandible && !isEmpty" class="expand-icon" :active="expanded" prevent-change color="var(--color-bg-primary-1)" :icon-name="'icon-'+(expanded?'compress':'expand')+'-blue'" @click-icon="clickExpand" />
					</div>
				</slot>
			</template>
		</AppCard>
	</div>

</template>

<script>
import VueApexCharts from "vue3-apexcharts";

import AppCard from "@/shared/components/AppCard.vue";
import AppInfoButton from "@/shared/components/AppInfoButton.vue";

import {useLanguageStore, useScriptStore, useSvgStore} from "@/shared/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import {toRaw} from "vue";
export default {
	setup() {
		const languageStore = useLanguageStore();
		let scriptStore = useScriptStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, getIcon: svgStore.get, loadScript: scriptStore.loadScript, toRaw}
	},
	components: {AppButtonIcon, ApexChart: VueApexCharts, AppCard, AppInfoButton},
	name: "AppChart",
	props: {
		title: {
			type: String,
			default: ''
		},
		caption: {type: String, default: ''},
		noCurrency: {type: Boolean, default: false},
		expandible: {type: Boolean, default: false},
		value: {default: 0},
		lastPeriodValue: {type: Number, default: 0},
		type: {
			type: String,
			default: 'line'
		},
		//Series array: array of series objects -> {name: name of the serie(String), data: array of Numbers}
		//data's indexes are correlated to xaxis indexes
		//example: [{name: 'Serie 1', data:[5, 10, 20,...]}, {name: 'Serie 2', data:[...]}, ]
		series: {
			type: Array,
			default: () => []
		},
		labels: {
			type: Array,
			default: () => []
		},
		//X axis values array: array of strings to be put on X axis
		//['Mon', 'Tue', 'Wed', ...]
		xaxis: {
			type: Object,
			default: () => { return {} }
		},
		colors: {
			type: Array,
			default: () => ['#6c33cb', '#dadada']
		},
		gradientColors: {
			type: Array,
			required: false,
			default: null
		},
		xaxisAnnotations: {
			type: Array,
			required: false,
			default: () => []
		},
		yaxisAnnotations: {
			type: Array,
			required: false,
			default: () => []
		},
		formatter: {
			type: Function,
			required: false,
		},
		hideYAxis: {
			type: Boolean,
			default: false
		},
		showLegend: {
			type: Boolean,
			default: false
		},
		showControls: {
			type: Boolean,
			default: false
		},
		fullWidth: {
			type: Boolean,
			default: false
		},
		height: {
			type: String,
			default: 'auto',
		},
		topScrollDiff: {
			type: Number,
			default: 0
		},
		badgeLive: {
			type: Boolean,
			default: false
		},
		dark: {
			type: Boolean,
			default: false
		},
		forceYAxisZero: {
			type: Boolean,
			default: false
		},
		infoButton: {
			type: String,
			required: false,
			default: null
		},
	},
	data() {
		return {
			timeoutRefresh: null,
			expanded: false,
			initialSeries: this.series,
		}
	},
	computed: {
		isEmpty() {
			return !this.series || !this.series.length;
		},
		increment() {
			if(!this.lastPeriodValue) { return null; }
			let difference = this.value - this.lastPeriodValue;
			return difference/this.lastPeriodValue*100;
		},
		parsedIncrement() {
			if(this.increment === null) { return null; }
			let finalValueRounded = Math.round(this.increment * 10) / 10;
			finalValueRounded = finalValueRounded % 1 ? finalValueRounded.toFixed(1) : finalValueRounded.toString();
			return finalValueRounded + '%';
		},
		finalIncrement() {
			if(this.increment === null) { return null; }
			if(this.parsedIncrement.length <= 6) {
				return this.parsedIncrement;
			}
			let returning = this.parsedIncrement.substring(0, 3);
			returning += returning.endsWith('.') ? '..' : '...';
			return returning + '%';
		},
		finalValue() {
			if(this.formatter && typeof this.value === 'number') {
				return this.formatter(this.value);
			} else {
				return this.value;
			}
		},
		finalLastPeriodValue() {
			if(this.formatter) {
				return this.formatter(this.lastPeriodValue);
			} else {
				return this.lastPeriodValue;
			}
		}
	},
	methods: {
		getIntelligentMaxValue(maxValue) {
			if (maxValue <= 0) return 0;
			
			// Add breathing room first (10%)
			let valueWithMargin = maxValue * 1.2;
			
			// Find the magnitude of the number
			let magnitude = Math.pow(10, Math.floor(Math.log10(valueWithMargin)));
			
			// Determine the rounding interval based on the first digit
			let normalized = valueWithMargin / magnitude;
			let interval;
			
			if (normalized <= 1) {
				interval = 0.1;
			} else if (normalized <= 2) {
				interval = 0.2;
			} else if (normalized <= 5) {
				interval = 0.5;
			} else {
				interval = 1;
			}
			
			// Round up to the nearest interval
			let roundedNormalized = Math.ceil(normalized / interval) * interval;
			
			return roundedNormalized * magnitude;
		},
		getOptions() {
			if(['pie', 'donut'].includes(this.type)) { return this.pieOptions() }
			if(['rangeArea'].includes(this.type)) { return this.rangeAreaOptions() }
			let finalOptions = {};
			finalOptions.colors = this.colors;
			finalOptions.dataLabels = {
				enabled: false,
			};
			finalOptions.series = this.series || [];
			finalOptions.tooltip = {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat, sans-serif',
				},
				custom: ({series, seriesIndex, dataPointIndex, w}) => {
					let seriesNames = w && w.globals && w.globals.seriesNames ? w.globals.seriesNames : [];
					let finalLabel = '';
					if(this.xaxis && this.xaxis.length && this.xaxis[dataPointIndex] && ['string', 'number'].includes(typeof this.xaxis[dataPointIndex])) {
						finalLabel = this.xaxis[dataPointIndex];
					} else if(this.xaxis.tooltipCustomFunction && w && w.config && w.config.series && w.config.series[seriesIndex] && w.config.series[seriesIndex].data && w.config.series[seriesIndex].data[dataPointIndex]) {
						finalLabel = this.xaxis.tooltipCustomFunction(w.config.series[seriesIndex].data[dataPointIndex]);
					}
					dataPointIndex = (dataPointIndex || 0).toString();
					let returningString = '<div class="chart-label">';
					returningString += '<div class="apexcharts-tooltip-title"><p>' + finalLabel + '</p></div>';

					if(series && series.length) {
						for(let i=0; i<series.length; i++) {
							let colorIndex = i % this.colors.length;
							let color = this.colors[colorIndex];
							let returningVal = this.formatter ? this.formatter(series[i][dataPointIndex] || 0) : series[i][dataPointIndex] || 0;
							returningString += '<div class="apexcharts-tooltip-serie"><p>' +
								'<span style="background: '+color+'" class="color-dot"></span>' +
								seriesNames[i] + ": <b>" + returningVal + '</b>' +
								'</p></div>';
						}
					}
					returningString += '</div>';
					return returningString;
				}
			};
			finalOptions.stroke = {
				curve: 'smooth',
				width: 4,
				lineCap: 'round',
			};
			finalOptions.fill = {opacity: 0.8};
			if(this.type === 'area') {
				finalOptions.fill.type = 'gradient';
				finalOptions.fill.gradient = {

					shade: this.dark ? 'dark' : 'light',
					shadeIntensity: this.dark ? 1 : 1,
					opacityFrom: 0.4,
					opacityTo: 0.6,
					stops: [0, 100]
				}
			} else if(this.gradientColors) {
				finalOptions.fill.type = 'gradient';
				finalOptions.fill.gradient = {
					gradientToColors: this.gradientColors,
					shadeIntensity: 1,
					type: 'horizontal',
					opacityFrom: 1,
					opacityTo: 1,
					stops: [0, 80]
				}
			}
			finalOptions.xaxis = {};
			if(this.xaxis.type || this.xaxis.categories) {
				finalOptions.xaxis = this.xaxis;
			} else {
				finalOptions.xaxis.tickAmount = 4;
				finalOptions.xaxis.axisTicks = { show: false };
				finalOptions.xaxis.type = 'category';
				finalOptions.xaxis.labels = {
					rotate: 0,
					hideOverlappingLabels: true,
					style: {
						fontFamily: 'Montserrat, sans-serif',
					}
				};
				finalOptions.xaxis.categories = this.xaxis || [];
			}
			if(this.hideYAxis || !this.series || !this.series.length) {
				finalOptions.yaxis = {
					floating: true,
					axisTicks: { show: false },
					axisBorder: { show: false },
					labels: { show: false }
				};
			} else {
				finalOptions.yaxis = {
					show: true,
					floating: false,
					opposite: true,
					tickAmount: 4,
					axisTicks: { show: false },
					axisBorder: { show: true },
					labels: {
						show: true,
						style: {
							fontFamily: 'Montserrat, sans-serif',
						},
						formatter: (val) => {
							return this.formatter ? this.formatter(val) : val;
						}
					}
				};
				if (this.forceYAxisZero) {
					finalOptions.yaxis.min = 0;
					finalOptions.yaxis.forceNiceScale = false;
					// Calculate max value from all series data
					let maxValue = 0;
					if (this.series && this.series.length) {
						this.series.forEach(serie => {
							if (serie.data && serie.data.length) {
								serie.data.forEach(value => {
									if (typeof value === 'number') {
										maxValue = Math.max(maxValue, value);
									}
								});
							}
						});
					}
					// Apply intelligent rounding algorithm
					finalOptions.yaxis.max = this.getIntelligentMaxValue(maxValue);
				}
			}
			finalOptions.grid = {
				show: this.series && this.series.length,
				borderColor: '#0752A31A',
			};
			finalOptions.noData = {
				text: this.getString('missing_data'),
				style: {
					color: 'var(--txt-0)',
					fontSize: '14px',
					fontFamily: 'Montserrat'
				}
			}
			finalOptions.legend = {
				show: !!this.showLegend || this.expanded,
			};
			finalOptions.chart = {};
			finalOptions.chart.redrawOnParentResize = false;
			finalOptions.chart.redrawOnWindowResize = false;
			finalOptions.chart.type = this.type;
			finalOptions.chart.toolbar = {
				show: this.showControls,
				autoSelected: null,
				tools: {
					reset: this.getIcon('iconX'),
					download: false,
				}
			};
			if(this.xaxisAnnotations || this.yaxisAnnotations) {
				finalOptions.annotations = {};
				if(this.yaxisAnnotations && this.yaxisAnnotations.length) {
					finalOptions.annotations.yaxis = this.yaxisAnnotations;
				}
				if(this.xaxisAnnotations && this.xaxisAnnotations.length) {
					finalOptions.annotations.xaxis = this.xaxisAnnotations;
				}
			}
			finalOptions.chart.animations = {
				enabled: true,
				easing: 'easeinout',
				speed: 500,
				animateGradually: {
					enabled: true,
					delay: 50
				},
				dynamicAnimation: {
					enabled: true,
					speed: 500
				}
			}
			return finalOptions;
		},
		pieOptions() {
			let finalOptions = {};
			finalOptions.labels = this.labels;
			finalOptions.colors = this.colors;
			finalOptions.dataLabels = {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat, sans-serif',
				},
			};
			if(this.type === 'donut') {
				finalOptions.fill = {
					type: "gradient",
					gradient: {
						shadeIntensity: 1,
						opacityFrom: 0.4,
						opacityTo: 1,
						stops: [0, 80]
					}
				}
			}
			finalOptions.chart = {
				type: this.type,
				redrawOnParentResize: false,
				redrawOnWindowResize: false,
				toolbar: {
					show: this.showControls,
					autoSelected: null,
					tools: {
						reset: this.getIcon('iconX'),
						download: false,
					}
				},
				animations: {
					enabled: true,
					easing: 'easeinout',
					speed: 500,
					animateGradually: {
						enabled: true,
						delay: 50
					},
					dynamicAnimation: {
						enabled: true,
						speed: 500
					}
				}
			};
			finalOptions.tooltip = {
				custom: ({series, seriesIndex, dataPointIndex, w}) => {
					seriesIndex = (seriesIndex || 0).toString();
					dataPointIndex = (dataPointIndex || 0).toString();
					let finalLabel = this.labels && this.labels.hasOwnProperty(seriesIndex) ? this.labels[seriesIndex] : '';
					let finalVal = series && series.hasOwnProperty(seriesIndex) ? series[seriesIndex] : 0;
					finalVal = finalVal && finalVal.hasOwnProperty(dataPointIndex) ? finalVal[dataPointIndex] : finalVal;
					if(this.formatter) {
						finalVal = this.formatter(finalVal);
					}
					return '<div class="pie-label">' +
						'<div class="pie-label-title"><p>' + finalLabel + '</p></div>' +
						'<div class="pie-label-body"><p><b>' + finalVal + '</b></p></div>' +
						'</div>'
				}
			}
			finalOptions.noData = {
				text: this.getString('missing_data'),
				style: {
					color: 'var(--txt-0)',
					fontSize: '14px',
					fontFamily: 'Montserrat'
				}
			}

			return finalOptions;
		},
		rangeAreaOptions() {
			//TODO this is customized for the weather chart, should be generalized
			let max = null;
			let min = null;
			if(this.series && this.series.length) {
				this.series[0].data.forEach(d => {
					if(d.y && d.y.length > 1) {
						if(max === null) {
							max = d.y[1];
							min = d.y[0];
						}
						max = Math.max(max, d.y[1]);
						max = Math.max(max, d.y[0]);
						min = Math.min(min, d.y[0]);
						min = Math.min(min, d.y[1]);
					}
				});
			}
			let colorsRep = ["r", "o", "y", "l", "b"];
			let colorsRepMap = {
				r: 'rgba(255, 102, 102, 0.5)',
				o: 'rgba(255, 153, 51, 0.5)',
				y: 'rgba(255, 255, 102, 0.5)',
				l: 'rgba(102,255,227,0.5)',
				b: 'rgba(71,162,230,0.5)',
			};
			let gradientColors = [];
			if(min !== null) {
				if(min > 20) {
					colorsRep = colorsRep.filter(c => ['r', 'o', 'y'].includes(c));
				} else if(min > 10) {
					colorsRep = colorsRep.filter(c => ['r', 'o', 'y', 'l'].includes(c));
				}
			}
			if(max !== null) {
				if(max < 10) {
					colorsRep = colorsRep.filter(c => ['y', 'l', 'b'].includes(c));
				} else if(max < 20) {
					colorsRep = colorsRep.filter(c => ['o', 'y', 'l', 'b'].includes(c));
				}
			}
			let colorsCount = Math.max(colorsRep.length, 2);
			gradientColors = colorsRep.map((c, i) => {
				return {
					color: colorsRepMap[c],
					offset: (i/(colorsCount-1))*100,
				}
			});
			return {
				series: this.series,
				chart: {
					type: this.type,
					animations: {
						enabled: true,
						easing: 'easeinout',
						speed: 500,
						animateGradually: {
							enabled: true,
							delay: 50
						},
						dynamicAnimation: {
							enabled: true,
							speed: 500
						}
					},
					toolbar: {
						show: this.showControls,
						autoSelected: null,
						tools: {
							reset: this.getIcon('iconX'),
							download: false,
						}
					},
					redrawOnParentResize: false,
					redrawOnWindowResize: false,
				},
				colors: this.colors,
				dataLabels: {
					enabled: false,
					style: {
						fontSize: '12px',
						fontFamily: 'Montserrat, sans-serif',
					},
				},
				fill: {
					opacity: [0.24, 0.3, 0.8],
					type: ['gradient', 'solid', 'solid'],
					gradient: {
						type: 'vertical',
						colorStops: gradientColors
					}
				},
				stroke: {
					curve: 'smooth',
					width: [0, 0, 4],
					lineCap: 'round',
				},
				legend: {
					show: !!this.showLegend || this.expanded,
				},
				markers: {
					hover: {
						sizeOffset: 5
					}
				},
				grid: {
					show: this.series && this.series.length,
					borderColor: '#0752A31A',
				},
				noData: {
					text: this.getString('missing_data'),
					style: {
						color: 'var(--txt-0)',
						fontSize: '14px',
						fontFamily: 'Montserrat'
					}
				},
				yaxis: [
					{
						seriesName: this.getString('temperature'),
						show: true,
						floating: false,
						opposite: false,
						tickAmount: 4,
						axisTicks: { show: false },
						axisBorder: { show: true },
						max: (max) => Math.max(max, 20),
						min: (min) => Math.min(min, 0),
						labels: {
							show: true,
							style: {
								fontFamily: 'Montserrat, sans-serif',
							},
							formatter: (val) => {
								return val.toFixed(1)+'°';
							}
						}
					},
					{
						seriesName: this.getString('rain'),
						show: false,
						floating: false,
						opposite: false,
						tickAmount: 4,
						axisTicks: { show: false },
						axisBorder: { show: true },
						min: 0,
						max: (max) => Math.max(max, 10),
						labels: {
							show: true,
							style: {
								fontFamily: 'Montserrat, sans-serif',
							},
							formatter: (val) => {
								return val.toFixed(0)+'mm';
							}
						}
					},
					{
						seriesName: this.getString('orders'),
						show: true,
						floating: false,
						opposite: true,
						tickAmount: 4,
						axisTicks: { show: false },
						axisBorder: { show: true },
						labels: {
							show: true,
							style: {
								fontFamily: 'Montserrat, sans-serif',
							},
							formatter: (val) => {
								return this.formatter ? this.formatter(val) : val;
							}
						}
					},
				],
				xaxis: {
					type: 'category',
					labels: {
						rotate: 0,
						hideOverlappingLabels: true,
						style: {
							fontFamily: 'Montserrat, sans-serif',
						}
					},
					tickAmount: 3,
					axisTicks: { show: false },
					categories: this.xaxis || [],
				},
				tooltip: {
					style: {
						fontSize: '12px',
						fontFamily: 'Montserrat, sans-serif',
					},
					custom: ({series, seriesIndex, dataPointIndex, w}) => {
						let seriesNames = w && w.globals && w.globals.seriesNames ? w.globals.seriesNames : [];
						let finalLabel = '';
						if(this.xaxis && this.xaxis.length && this.xaxis[dataPointIndex] && ['string', 'number'].includes(typeof this.xaxis[dataPointIndex])) {
							finalLabel = this.xaxis[dataPointIndex];
						} else if(this.xaxis.tooltipCustomFunction && w && w.config && w.config.series && w.config.series[seriesIndex] && w.config.series[seriesIndex].data && w.config.series[seriesIndex].data[dataPointIndex]) {
							finalLabel = this.xaxis.tooltipCustomFunction(w.config.series[seriesIndex].data[dataPointIndex]);
						}
						dataPointIndex = (dataPointIndex || 0).toString();
						let returningString = '<div class="chart-label">';
						returningString += '<div class="apexcharts-tooltip-title"><p>' + finalLabel + '</p></div>';

						if(series && series.length) {
							let initialSeries = w && w.globals && w.globals.initialSeries ? w.globals.initialSeries : [];
							for(let i=0; i<series.length; i++) {
								let colorIndex = i % this.colors.length;
								let color = this.colors[colorIndex];
								if(i === 0 && initialSeries.length > 0) {
									let data = initialSeries[0].data[dataPointIndex];
									if(data && data.y && data.y.length > 1) {
										let min = data.y[0];
										let max = data.y[1];
										returningString += '<div class="apexcharts-tooltip-serie"><p>' +
											'<span style="background: '+color+'" class="color-dot"></span>' +
											seriesNames[i] + ": <b>" + min.toFixed(1) + '°</b> - <b>' + max.toFixed(1) + '°</b>' +
											'</p></div>';
										continue;
									}
								} else if(i === 1) {
									let value = series[i][dataPointIndex] || 0;
									returningString += '<div class="apexcharts-tooltip-serie"><p>' +
										'<span style="background: '+color+'" class="color-dot"></span>' +
										seriesNames[i] + ": <b>" + (value > 0 ? value.toFixed(0)+'mm' : '-') + '</b>' +
										'</p></div>';
									continue;
								}

								let returningVal = this.formatter ? this.formatter(series[i][dataPointIndex] || 0) : series[i][dataPointIndex] || 0;
								returningString += '<div class="apexcharts-tooltip-serie"><p>' +
									'<span style="background: '+color+'" class="color-dot"></span>' +
									seriesNames[i] + ": <b>" + returningVal + '</b>' +
									'</p></div>';
							}
						}
						returningString += '</div>';
						return returningString;
					}
				},
			}
		},
		clickExpand() {
			this.expanded = !this.expanded;
			setTimeout(this.reset, 100);
			setTimeout(this.reset, 200);
			setTimeout(this.reset, 300);
			setTimeout(this.reset, 400);
			setTimeout(this.reset, 500);
			if(this.expanded) {
				setTimeout(this.scrollToTop, 150);
			}
		},
		scrollToTop() {
			let mainContent = document.querySelector('.main-content');
			if(mainContent) {
				mainContent.scrollTo({
					top: this.$el.offsetTop - this.topScrollDiff,
					behavior: 'smooth'
				});
			}
		},
		reset() {
			let chart = this.$refs['chart'];
			if(chart && chart.chart && chart.chart.resetSeries && this.series.length > 0) {
				chart.chart.resetSeries();
			}
		},
		destroy() {
			let chart = this.$refs['chart'];
			if(chart && chart.chart && chart.chart.destroy) {
				chart.chart.destroy();
				chart.destroy();
			}
		},
		resizeRefresh() {
			if(this.timeoutRefresh) {
				clearTimeout(this.timeoutRefresh);
			}
			this.timeoutRefresh = setTimeout(this.reset, 100);
		},
	},
	mounted() {
		window.addEventListener('resize', this.resizeRefresh);
	},
	beforeUnmount() {
		window.removeEventListener('resize', this.resizeRefresh);
		this.destroy();
	},
}
</script>

<style lang="scss" scoped>
.chart-container {
	transition: margin-top .35s ease-in-out, margin-bottom .35s ease-in-out, width .3s ease-in-out, min-width .3s ease-in-out, max-width .3s ease-in-out;
	min-width: 500px;

	&.expandible .header-title-container h3 {
		margin-right: 5px;
	}

	.header-title-container {
		display: flex;
		align-items: center;
		gap: 5px
	}

	&.full-width { min-width: 100%; }
	.card-container {
		.chart-svg-container {
			position: relative;
		}
		.badge-live-container {
			position: relative;
			box-shadow: 0 2px 1px var(--color-box-shadow-1);
			margin-left: 25px;
			margin-bottom: -36px;
			--borderWidth: 2px;
			border-radius: 13px;
			width: 60px;
			pointer-events: none;
			user-focus: none;
			user-select: none;
			z-index: 10;

			* {
				user-focus: none;
				user-select: none;
				pointer-events: none;
			}

			.badge-live {
				z-index: 1;
				position: relative;
				background: var(--color-bg);
				border-radius: 13px;
				text-transform: uppercase;
				font-size: 14px;
				text-align: center;
				font-weight: 700;
				color: $primary;
				padding: 2px 0;
			}

			&:after {
				content: '';
				position: absolute;
				top: calc(-1 * var(--borderWidth));
				left: calc(-1 * var(--borderWidth));
				height: calc(100% + var(--borderWidth) * 2);
				width: calc(100% + var(--borderWidth) * 2);
				background: linear-gradient(60deg, #0752A3, #28EDBF, #8f4dfe, #FC2291
				, #FDC700);
				border-radius: 13px;
				z-index: 0;
				animation: animatedgradient 8s ease-in-out alternate-reverse infinite;
				background-size: 300% 300%;
			}


			@keyframes animatedgradient {
				0% {
					background-position: 0% 50%;
				}
				50% {
					background-position: 300% 100%;
				}
				100% {
					background-position: 0% 50%;
				}
			}

		}
		.data-container {
			width: fit-content;
			background: var(--color-bg-primary-1);
			border-radius: 13px;
			margin-left: 12px;
			margin-bottom: -60px;
			z-index: 1;
			box-shadow: 0 2px 1px var(--color-box-shadow-3);
			position: relative;
			padding: 6px 10px;
			pointer-events: none;
			max-width: 70%;

			.container-caption-head {
				flex-wrap: nowrap;
				gap: 8px;
			}
			.container-caption {
				flex-wrap: wrap;
				gap: 8px;
			}

			p.info-value {
				font-size: 26px;
				line-height: 28px;
				white-space: nowrap;
			}
			p.info-last-period-value {
				color: #9B9B9B;
				font-size: 16px;
				line-height: 20px;
				white-space: nowrap;
				margin-left: auto;
			}
			p.info-caption {
				font-size: 14px;
				line-height: 22px;
				user-select: none;
				white-space: nowrap;
				overflow: hidden;
			}
			p.info-increment:not(:empty) {
				font-size: 12px;
				line-height: 14px;
				border-radius: 8px;
				padding: 4px 5px;
				background: rgb(215,247,194);
				color: rgb(0,105,8);
				max-width: 55px;
				user-select: none;
				@include truncateString(1);

				&.negative {
					background: rgb(252,237,185);
					color: rgb(168,44,0);
				}
			}
		}
		.empty-chart-image {
			@include centered;
			flex-direction: column;
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			min-height: 200px;
			opacity: 0;
			pointer-events: none;

			&:deep(svg) {
				width: 100%;
				height: 100%;
				max-width: 100%;
				max-height: calc(100% - 50px);
				opacity: .8;
				margin-bottom: 10px;
			}
		}

		&:not(.no-data) {
			&:deep(.card-body) {
				padding-bottom: 5px;
			}
			&.no-padding:deep(.card-body) {
				padding-right: 0;
			}
		}

		.vue-apexcharts {
			opacity: 1;
			:deep(.apexcharts-toolbar) {

				//generic custom style of icons
				.apexcharts-pan-icon,
				.apexcharts-menu-icon,
				.apexcharts-reset-icon,
				.apexcharts-zoom-icon,
				.apexcharts-zoomin-icon,
				.apexcharts-zoomout-icon {
					svg {
						opacity: .6;
						fill: var(--color-svg-primary);
						path, rect {
							&.fill { fill: var(--color-svg-primary); }
							&.stroke { stroke: var(--color-svg-primary); }
						}
					}
					&.apexcharts-selected svg, &:hover svg{
						opacity: .8;
					}
					&.apexcharts-selected {
						svg {
							opacity: 1;
							fill: $secondary;
							path, rect {
								&.fill { fill: $secondary; }
								&.stroke { stroke: $secondary; }
							}
						}
					}
				}

				//specific style of exceptional icons (stroke instead of fill)
				.apexcharts-pan-icon {
					svg {
						stroke: var(--color-svg-primary);
						fill: none;
					}
					&.apexcharts-selected svg {
						stroke: var(--color-secondary);
						fill: none;
					}
				}

			}
			:deep(text) {
				fill: var(--color-txt);
			}
			:deep(line) {
				stroke: var(--color-alpha-1);
			}
			:deep(tspan) {
				fill: var(--color-txt);
			}
			:deep(.apexcharts-tooltip) {
				padding-bottom: 8px;
				border-radius: 13px;
				background-color: var(--color-bg-primary-1);
				box-shadow: 0 3px 5px var(--color-box-shadow-3);
				border: none;

				.apexcharts-tooltip-title {
					padding: 4px 8px;
					background-color: var(--color-bg-primary-2);
					border-bottom: none;
				}
				.apexcharts-tooltip-serie {
					padding: 4px 8px 0;

					.color-dot {
						display: inline-block;
						width: 8px;
						height: 8px;
						border-radius: 50%;
						margin-right: 6px;
					}
				}
				.apexcharts-tooltip-y-group {
					padding: 0;
				}

				span, p {
					font-size: 12px;
					line-height: 14px;
					font-family: 'Montserrat', sans-serif;
				}
			}
			:deep(.apexcharts-xaxistooltip) {
				background-color: var(--color-bg-primary-2);
				border-color: var(--color-primary);
				border-radius: 13px;

				&:before {
					border-bottom-color: var(--color-primary);
				}
				&:after {
					border-bottom-color: var(--color-bg-primary-2);
				}
			}
			:deep(.pie-label) {
				border-radius: 13px;
				background-color: var(--color-bg);
				box-shadow: 0 3px 5px var(--color-box-shadow-3);
				border: none;
				opacity: 1;

				.pie-label-title {
					padding: 4px 8px;
					background-color: var(--color-bg-primary-2);
					border-bottom: none;
					box-shadow: none;
				}
				.pie-label-body {
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 6px;
					box-shadow: none;
				}
			}
			:deep(svg.apexcharts-svg) {
				overflow: unset;
			}
		}

		.empty-chart-image, .vue-apexcharts {
			transition: opacity .2s ease;
		}

		&.no-data {
			.vue-apexcharts {
				opacity: 0;
			}
			.data-container {
				display: none;
			}
			.empty-chart-image {
				opacity: 1;
			}
		}
	}

	&.pie {
		.vue-apexcharts {
			:deep(.apexcharts-tooltip) {
				padding-bottom: 0;
			}
			:deep(.pie-label) {
				min-width: 70px;
				border-radius: 13px;
				background-color: var(--color-bg);
				box-shadow: 0 3px 5px var(--color-box-shadow-3);
				border: none;

				.pie-label-title {
					padding: 4px 8px;
					background-color: var(--color-bg-primary-2);
					border-bottom: none;
					box-shadow: none;
				}
				.pie-label-body {
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 6px;
					box-shadow: none;
				}
			}
		}
	}

	@media (max-width: 768px) {
		&:not(.full-width) { min-width: 360px; }

		.card-container {
			.data-container {
				p.info-value {
					font-size: 22px;
					line-height: 24px;
				}
				p.info-last-period-value {
					font-size: 14px;
					line-height: 16px;
				}
				p.info-caption {
					font-size: 12px;
					line-height: 14px;
					padding-right: 6px;
				}
				p.info-increment {
					font-size: 10px;
					line-height: 12px;
				}
			}
		}
	}
	@media (max-width: 600px) {
		min-width: unset;
		.card-container {
			.data-container {
				margin-bottom: -40px;
			}
		}
	}
	@media (max-width: 520px) {
		.expand-icon {
			display: none;
		}
	}

	&.expanded {
		width: 100%;
		min-width: 100%;
		max-width: 100%;
	}
}
</style>